import React from 'react';
import BaseFactory from './BaseFactory';
import { isEmptyString, arrayToEnglish, objectToEnglish } from '../../../core/util/string';
import { isEmptyObject } from '../../../core/util/object';
import { DAVAM_URGENT_CARE } from '../../../config/locations';
import Link from '../../Link';
import { getCityDirectoryUrl } from '../../../core/util/seo';
import { getRelativeDayName } from '../../../core/util/date';
import moment from 'moment-timezone';
import {
  validHighlyRatedFor,
  validPrices,
  hasHours,
  getOpenHoursString,
  getCollectedUrgentCareAcceptedInsuranceTypes,
  isSelfPayOnly,
} from '../../../core/util/location';
import { NoTopMarginParagraph, NoBottomMarginParagraph, hasCustomDescription } from './util';
import { BASE_URL } from '../../../config';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class DefaultCDPInfo extends BaseFactory {
  getDescription() {
    return (
      `Book an appointment at ${this.clinicName}, located at ${this.address} ` +
      `in ${this.city}, ${this.state}. ${this.clinicName} has ` +
      `${this.ratingCount} reviews on Solv.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `urgent care ${this.city}, urgent care ${this.zip}, walk-in clinic ${this.city}, ` +
      `walk-in clinic ${this.zip}, ${this.city} ${this.state} clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    const appointmentDay = moment.tz(this.location.timeZone).valueOf();

    let ratingString = '';
    if (!isEmptyObject(this.location.scores) && this.location.scores.aggregated.length > 0) {
      const rating = this.location.scores.aggregated[0].score;

      ratingString = `This location is rated ${rating.toFixed(1)} stars by patients `;
    }

    const highlyRated = validHighlyRatedFor(this.location.scores.web).map(
      thing => `${thing.label.toLowerCase()}`
    );

    let highlyRatedString = '';
    if (highlyRated.length > 0) {
      highlyRatedString = ` and receives positive reviews for ${arrayToEnglish(highlyRated)}.`;
    } else if (!isEmptyString(ratingString)) {
      ratingString += '.';
    }

    const serviceSortValue = value => (/other/.test(value) ? 1 : 0);

    const services = Object.keys(this.location.services || {})
      .map(service => service.toLowerCase())
      .sort((a, b) => serviceSortValue(a) - serviceSortValue(b));

    let servicesString = '';
    if (services.length > 0) {
      servicesString = `They also offer ${arrayToEnglish(services)} on-site.`;
    }

    let priceRangeString = '';
    let lowPrice = 0;
    let highPrice = 0;
    const attributes = validPrices(this.location.attributes);
    const priceValues = Object.values(attributes).map(attribute =>
      parseInt(attribute.attribute_value, 10)
    );
    if (priceValues.length === 1) {
      priceRangeString = `Patients typically pay about $${priceValues[0]} for common services and `;
    }

    if (priceValues.length > 1) {
      lowPrice = Math.min.apply(null, priceValues.filter(val => !isNaN(val)));
      highPrice = Math.max.apply(null, priceValues.filter(val => !isNaN(val)));
      priceRangeString = `Patients typically pay between $${lowPrice} - $${highPrice} for common services and `;
    }

    const acceptedInsuranceTypes = getCollectedUrgentCareAcceptedInsuranceTypes(this.location, {
      formatInsurerType: true,
    });
    const acceptsInsurance = !isSelfPayOnly(this.location);
    const insurancesString = acceptsInsurance
      ? `accepts insurances including ${objectToEnglish(acceptedInsuranceTypes)}`
      : 'does not accept insurance';

    const prices = Object.values(attributes).map(
      attr => `$${attr.attribute_value} ${attr.display_name}`
    );
    let pricesString = '';
    if (prices.length > 0) {
      pricesString = ` and typical prices are ${objectToEnglish(prices)}`;
    }

    const openHoursString = getOpenHoursString({
      location: this.location,
      date: appointmentDay,
    });

    const getHoursText = () => {
      if (!hasHours(this.location)) {
        return '';
      }

      return (
        `They are open ${getRelativeDayName(appointmentDay).toLowerCase()} ` +
        `from ${openHoursString}, helping you get immediate care.`
      );
    };

    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is an urgent care center and medical clinic located at{' '}
          {this.fullAddress} in{' '}
          {this.isLinkable ? (
            <Link to={`${BASE_URL}${getCityDirectoryUrl(this.state, this.city.toLowerCase())}`}>
              {this.city}, {this.state}
            </Link>
          ) : (
            <span>
              {this.city}, {this.state}
            </span>
          )}
          . {getHoursText()}
        </NoTopMarginParagraph>
        <p>
          While {this.clinicName} is a walk-in clinic that is open late and after hours, patients
          can also conveniently book online using Solv.
        </p>
        <NoBottomMarginParagraph>
          {ratingString}
          {highlyRatedString} {servicesString} {this.cityGuideContent}
        </NoBottomMarginParagraph>

        {this.location.id === DAVAM_URGENT_CARE ? (
          <p>
            Davam Urgent Care accepts most insurances. Please contact the clinic for specific
            insurance information.
          </p>
        ) : (
          <p>
            {priceRangeString} {this.location.displayNamePrimary} {insurancesString}. Cash payments
            are accepted {pricesString}.
          </p>
        )}
      </span>
    );
  }
}

export default DefaultCDPInfo;
